import { useMutation, useQuery } from "@tanstack/react-query";
import { Copy, Flag, FlagOff, QrCode } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import ConfirmDialog from "@/components/confirm-dialog";
import { CopyableAddress } from "@/components/copyable-address";
import { SiteEntrancesSection } from "@/components/site-entrances-section";
import { SiteSignInsSection } from "@/components/site-signins-section";
import { DetailSkeleton } from "@/components/skeletons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Hint } from "@/components/ui/tooltip";
import { intakeAddress } from "@/lib/intake-address";
import { formatPublicCode } from "@/lib/public-code";
import { formatSiteStatus, getSiteStatusBadgeVariant, isSiteAccessActive } from "@/lib/site-status";
import { trpc } from "@/utils/trpc";

export default function SiteLaunchPad() {
  const { id } = useParams<{ id: string }>();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const site = useQuery({
    ...trpc.getSite.queryOptions({ id: id || "" }),
    enabled: Boolean(id)
  });

  const deactivateMutation = useMutation({
    ...trpc.deactivateSite.mutationOptions(),
    onSuccess: () => {
      toast.success("Site deactivated");
      void site.refetch();
      setShowDeactivateDialog(false);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      toast.error(message || "Failed to deactivate site");
    }
  });

  const reactivateMutation = useMutation({
    ...trpc.reactivateSite.mutationOptions(),
    onSuccess: () => {
      toast.success("Site reactivated");
      void site.refetch();
      setShowDeactivateDialog(false);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      if (message?.includes("No available paid site slots")) {
        toast.error("No paid site slots available", {
          description: "Deactivate another site or update your subscription to add more seats."
        });
        return;
      }
      toast.error(message || "Failed to reactivate site");
    }
  });

  const handleConfirmStatusChange = async () => {
    if (!id) return;
    if (isSiteAccessActive(site.data?.status)) {
      void deactivateMutation.mutateAsync({ id });
    } else {
      void reactivateMutation.mutateAsync({ id });
    }
  };

  if (site.isLoading) {
    return (
      <DetailSkeleton gridClassName="lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start" />
    );
  }

  if (site.isError || !site.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Site not found</AlertTitle>
        <AlertDescription>
          This site may have been removed. Return to{" "}
          <Link to="/sites" className="font-medium underline underline-offset-4">
            all sites
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  const siteAccessActive = isSiteAccessActive(site.data.status);

  return (
    <>
      <div className="space-y-6 lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start lg:gap-8 lg:space-y-0">
        <aside className="space-y-6 lg:sticky lg:top-0 lg:self-start">
          <Card>
            <CardHeader>
              <div className="space-y-1">
                <CardTitle>{site.data.name}</CardTitle>
                <CardDescription>
                  {site.data.address || "Site settings and sign-in activity"}
                </CardDescription>
                <Badge
                  variant={getSiteStatusBadgeVariant(site.data.status)}
                  size="sm"
                  className="w-fit"
                >
                  {formatSiteStatus(site.data.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <Field label="Address">{site.data.address || "No address set"}</Field>
                <Field label="Site code">
                  <CopyableCode value={site.data.publicCode} />
                </Field>
                <Field label="Site email">
                  <CopyableAddress
                    label="site email"
                    tooltip="Copy site email"
                    address={intakeAddress(site.data.publicCode)}
                  />
                </Field>
                <Field label="Geofence">{site.data.geofenceDistance}m</Field>
                <Field label="Plus code">{site.data.plusCode || "Not set"}</Field>
                <Field label="Entrances">{site.data.entrances?.length ?? 0}</Field>
              </dl>
            </CardContent>
            <CardFooter className="flex-col gap-2">
              <Button asChild variant="light" className="w-full">
                <Link to={`/sites/${id}/edit`}>Edit site</Link>
              </Button>
              <Button asChild variant="light" className="w-full">
                <a href={`/sites/${id}/qr`} target="_blank" rel="noopener noreferrer">
                  <QrCode className="h-4 w-4" />
                  Site QR code
                </a>
              </Button>
            </CardFooter>
          </Card>
        </aside>

        <main className="min-w-0 space-y-6">
          <SiteEntrancesSection
            siteId={site.data.id}
            siteName={site.data.name}
            entrances={site.data.entrances || []}
          />
          <SiteSignInsSection siteId={site.data.id} siteName={site.data.name} />

          <Card>
            <CardHeader>
              <CardTitle>Site access</CardTitle>
              <CardDescription>
                {siteAccessActive
                  ? "Deactivate this site to stop new worker sign-ins."
                  : "Activate this site to allow worker sign-ins again."}
              </CardDescription>
              <CardAction>
                <Button
                  type="button"
                  variant={siteAccessActive ? "destructive" : "default"}
                  onClick={() => setShowDeactivateDialog(true)}
                >
                  {siteAccessActive ? (
                    <FlagOff className="h-4 w-4" />
                  ) : (
                    <Flag className="h-4 w-4" />
                  )}
                  {siteAccessActive ? "Deactivate site" : "Activate site"}
                </Button>
              </CardAction>
            </CardHeader>
          </Card>
        </main>
      </div>

      <ConfirmDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
        title={siteAccessActive ? "Deactivate this site?" : "Activate this site?"}
        description={
          <div>
            <strong>
              {siteAccessActive
                ? "This action will mark the site as inactive."
                : "This action will mark the site as active."}
            </strong>
            {siteAccessActive ? (
              <ul className="list-disc list-inside mt-2">
                <li>Workers will not be able to sign in</li>
                <li>The QR code will no longer allow access</li>
                <li>You can activate this site later</li>
              </ul>
            ) : (
              <ul className="list-disc list-inside mt-2">
                <li>Workers will be able to sign in</li>
              </ul>
            )}
          </div>
        }
        confirmLabel={siteAccessActive ? "Yes, deactivate" : "Yes, activate"}
        pendingLabel={siteAccessActive ? "Deactivating..." : "Activating..."}
        pending={deactivateMutation.isPending || reactivateMutation.isPending}
        onConfirm={handleConfirmStatusChange}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-[0.65rem] uppercase tracking-[0.08em] text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  );
}

function CopyableCode({ value }: { value?: string | null }) {
  const formattedCode = formatPublicCode(value);

  const handleCopy = async () => {
    if (!formattedCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formattedCode);
      toast.success("Site code copied");
    } catch (error) {
      console.error("Failed to copy site code", error);
      toast.error("Failed to copy site code");
    }
  };

  if (!formattedCode) {
    return <span className="text-muted-foreground">Code pending</span>;
  }

  return (
    <Hint content="Copy site code">
      <button
        type="button"
        onClick={() => void handleCopy()}
        aria-label="Copy site code"
        className="font-meta inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground transition hover:bg-muted focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
      >
        <span>{formattedCode}</span>
        <Copy className="h-4 w-4" aria-hidden="true" />
      </button>
    </Hint>
  );
}
